// Captura de atribucion (utm_*, cta_clicked, ref_host, landing_path).
// El app solo RECIBE: no interpreta, no mapea ni decide fuente; eso lo hace el CRM.
// Todo acceso a localStorage/sessionStorage va en try/catch (Safari privado, almacenamiento
// bloqueado): si falla, se sigue con lo que haya en la URL y nada rompe.

const ATTR_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'cta_clicked', 'ref_host', 'landing_path'];
// Senal fuerte: parametros explicitos del sitio o de un anuncio
const STRONG_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'cta_clicked'];
// Mismos largos que las columnas del API
const MAX_LEN = {
  utm_source: 155,
  utm_medium: 155,
  utm_campaign: 155,
  utm_content: 155,
  utm_term: 155,
  cta_clicked: 200,
  ref_host: 155,
  landing_path: 500,
};
// El primer alias que traiga valor gana
const ALIASES = { cta_clicked: ['cta_clicked', 'ctaClicked'], landing_path: ['landing_path', 'landing'] };
const TTL_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
const LOCAL_KEY = 'ap_attribution'; // localStorage: ultimo toque con senal, vence a los TTL_DAYS
const SESSION_KEY = 'ap_landing'; // sessionStorage: primera carga de ESTA pestana, aunque no traiga senal
// Parametros que nunca deben quedar en landing_path: tokens, ids de sesion, referencias de pago y
// datos personales de prellenado (email=, phone=, name=) que agregan las herramientas de campanas
const SENSITIVE_QUERY =
  /^(token|uid|code|password|pass|key|secret|auth|otp|session|state|signature|sig|hmac|ref|error|verificationsessionid|email|e-?mail|correo|phone|tel|telefono|celular|whatsapp|name|nombre|apellidos?)$|token$|key$|secret$|session_?id$|mail$|phone$/i;
// Solo se captura en las rutas publicas (sin sesion). Las rutas con sesion reciben retornos de
// terceros (Didit, banco) e ids de otros clientes en la URL, y eso no es atribucion.
// Sin distinguir mayusculas: react-router v5 tampoco lo hace.
const PUBLIC_PATHS = /^\/(login|register|forgot-password|reset-password|verify-code|page-not-found)?\/?$/i;
const BASENAME = (process.env.REACT_APP_BASENAME || '').replace(/\/+$/, '').toLowerCase();

// Ruta de la app sin el basename del router
const appPath = () => {
  const pathname = window.location.pathname.toLowerCase();
  return BASENAME && pathname.indexOf(BASENAME) === 0 ? pathname.slice(BASENAME.length) || '/' : pathname;
};

// Limpia lo que el API rechazaria (caracteres de control, NUL, surrogates sueltos) y corta por
// puntos de codigo, no por unidades UTF-16, para no partir un emoji a la mitad
const cleanText = (text, maxLen) =>
  Array.from(String(text || ''))
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      const isControl = code < 32 || code === 127;
      const isLoneSurrogate = ch.length === 1 && code >= 0xd800 && code <= 0xdfff;
      return !isControl && !isLoneSurrogate;
    })
    .slice(0, maxLen)
    .join('')
    .trim();

// ---- storage (cada acceso en su propio try/catch) ----

const readEntry = (storeName, key) => {
  try {
    const entry = JSON.parse(window[storeName].getItem(key));
    return entry && entry.v ? entry : null;
  } catch (e) {
    return null;
  }
};

const writeEntry = (storeName, key, entry) => {
  try {
    window[storeName].setItem(key, JSON.stringify(entry));
  } catch (e) {
    // almacenamiento bloqueado o lleno: se sigue sin guardar
  }
};

const removeEntry = (storeName, key) => {
  try {
    window[storeName].removeItem(key);
  } catch (e) {
    // almacenamiento bloqueado: nada que borrar
  }
};

const readLocal = () => {
  const entry = readEntry('localStorage', LOCAL_KEY);
  return entry && Date.now() - entry.ts <= TTL_DAYS * DAY_MS ? entry : null;
};

const readSession = () => readEntry('sessionStorage', SESSION_KEY);

// ---- lectura de la carga actual ----

// Primer alias con valor, con trim y recortado; sin cambiar mayusculas ni normalizar
const readParam = (params, key) => {
  const names = ALIASES[key] || [key];
  const value = names.map((name) => cleanText(params.get(name), MAX_LEN[key])).find(Boolean);
  return value || '';
};

// Host del referrer tal cual; '' si no hay, no parsea o es el propio app (recarga o link interno)
const referrerHost = () => {
  try {
    const host = new URL(document.referrer).hostname;
    return host === window.location.hostname ? '' : cleanText(host, MAX_LEN.ref_host);
  } catch (e) {
    return '';
  }
};

const paramName = (pair) => new URLSearchParams(pair).keys().next().value || '';

// URL completa (con origen) de la carga actual, sin parametros sensibles:
// el panel distingue "aterrizo en el app" (https://) de "aterrizo en el sitio" (/ruta)
const ownLandingUrl = () => {
  const { origin, pathname, search } = window.location;
  const isReset = /\/reset-password\/?$/.test(pathname); // lleva token y uid: se descarta todo el query
  const pairs = isReset ? [] : search.replace(/^\?/, '').split('&');
  const query = pairs.filter((pair) => pair && !SENSITIVE_QUERY.test(paramName(pair))).join('&');
  const suffix = query ? `?${query}` : '';
  return cleanText(`${origin}${pathname}${suffix}`, MAX_LEN.landing_path);
};

// Candidato de la carga actual; strength: 2 params explicitos, 1 solo referrer/ref_host/landing, 0 directo.
// useReferrer=false al enviar: document.referrer es el de la carga completa de la pestana, que pudo ser
// una ruta con sesion (retorno del banco o de Didit) y no debe colarse en un registro posterior.
const captureFromCurrent = (useReferrer = true) => {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = {};
  ATTR_KEYS.forEach((key) => {
    fromUrl[key] = readParam(params, key);
  });
  const referrer = useReferrer ? referrerHost() : '';

  let strength = 0;
  if (fromUrl.ref_host || fromUrl.landing_path || referrer) strength = 1;
  if (STRONG_KEYS.some((key) => fromUrl[key])) strength = 2;

  return {
    v: 1,
    ts: Date.now(),
    strength,
    ...fromUrl,
    ref_host: fromUrl.ref_host || referrer,
    landing_path: fromUrl.landing_path || ownLandingUrl(),
  };
};

// ---- API publica ----

// Una vez por carga completa (src/index.js), en cualquier ruta publica
export const captureLanding = () => {
  if (!PUBLIC_PATHS.test(appPath())) return; // con sesion no hay atribucion que capturar
  const candidate = captureFromCurrent();
  if (candidate.strength >= 1) {
    const stored = readLocal();
    if (!stored || stored.strength < candidate.strength) {
      // Primer toque entre senales iguales; una con parametros reemplaza a una que solo traia referrer
      writeEntry('localStorage', LOCAL_KEY, candidate);
    } else if (stored.strength === candidate.strength) {
      // Misma fuerza: se conserva el primer toque, pero se renueva la ventana de TTL_DAYS
      writeEntry('localStorage', LOCAL_KEY, { ...stored, ts: candidate.ts });
    }
  } else if (!readSession()) {
    // Carga directa: write-once por pestana, nunca toca LOCAL
    writeEntry('sessionStorage', SESSION_KEY, candidate);
  }
};

// Al enviar el registro: todo sale de UNA sola captura, nunca se mezclan tuplas
export const getAttribution = () => {
  const entry = readLocal() || readSession() || captureFromCurrent(false);
  const attribution = {};
  ATTR_KEYS.forEach((key) => {
    attribution[key] = entry[key] || undefined;
  });
  return attribution;
};

// Tras un POST /signup exitoso, para que la siguiente persona en este navegador no herede la atribucion
export const clearAttribution = () => {
  removeEntry('localStorage', LOCAL_KEY);
  removeEntry('sessionStorage', SESSION_KEY);
};

export default { captureLanding, getAttribution, clearAttribution };
